/**
 * Wave 3.59 — Receive Recut dialog.
 *
 * Called from the Broken Recut Issues list when the operator receives
 * material back from the cutter / vendor. Wraps the atomic
 * `POST /api/broken-packet-issues/{id}/receive` endpoint that:
 *   1. Restores the broken packet balance,
 *   2. Immediately runs the Wave 3.47 `execute_recut` engine to route
 *      the returned material into either an existing or new normal
 *      packet (value preserved; carat loss captured).
 *
 * Target-mode `existing` presents a searchable dropdown of same-material
 * normal packets sharing the same classification. Mode `new` collects
 * the fields required by the recut engine (classification, valuation
 * mode, etc.).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { formatCarats } from '@/lib/format';
import { extractError } from '@/lib/errors';
import { PackageCheck } from 'lucide-react';

const MATERIAL_ENDPOINT = {
  diamond:     '/diamonds',
  color_stone: '/color-stones',
  pearl:       '/pearls',
};

export default function ReceiveRecutDialog({ open, onOpenChange, issue, onReceived }) {
  const material = issue?.material_kind || 'diamond';
  const base = MATERIAL_ENDPOINT[material] || MATERIAL_ENDPOINT.diamond;

  const issuedCt = Number(issue?.issued_carats || 0);
  const issuedPcs = Number(issue?.issued_pieces || 0);

  const [mode, setMode] = useState('new');   // 'existing' | 'new'
  const [form, setForm] = useState({
    returned_carats: '',
    returned_pieces: '',
    target_packet_id: '',
    new_classification_id: '',
    new_valuation_mode: 'running_avg',
    new_fixed_rate: '',
    new_description: '',
    note: '',
  });
  const [classifications, setClassifications] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !issue) return;
    setMode('new');
    setForm({
      returned_carats: String(issuedCt),
      returned_pieces: String(issuedPcs),
      target_packet_id: '',
      new_classification_id: issue.classification_id || '',
      new_valuation_mode: 'running_avg',
      new_fixed_rate: '',
      new_description: '',
      note: '',
    });
    (async () => {
      setLoading(true);
      try {
        const params = { company_id: issue.company_id };
        const [cls, pkts] = await Promise.all([
          api.get(`${base}/classifications`, { params }),
          api.get(`${base}/packets`, { params: { ...params, packet_type: 'normal' } }),
        ]);
        setClassifications(Array.isArray(cls.data) ? cls.data : []);
        const rows = Array.isArray(pkts.data) ? pkts.data : (pkts.data?.items || pkts.data?.packets || []);
        setCandidates(rows.filter((p) => (p.packet_type || 'normal') !== 'broken'));
      } catch {
        // best-effort
      } finally { setLoading(false); }
    })();
  }, [open, issue?.id, base, issuedCt, issuedPcs, issue]);

  const rct = Number(form.returned_carats) || 0;
  const rpcs = Number(form.returned_pieces) || 0;
  const cutLoss = useMemo(() => Math.max(0, issuedCt - rct), [issuedCt, rct]);
  const overIssued = rct - issuedCt > 1e-9 || rpcs > issuedPcs;

  const submit = async () => {
    if (rct <= 0 || rpcs <= 0) {
      toast.error('Returned carats & pieces must both be greater than zero');
      return;
    }
    if (overIssued) {
      toast.error('Returned quantity cannot exceed what was issued');
      return;
    }
    const payload = {
      returned_carats: rct,
      returned_pieces: rpcs,
      target_mode: mode,
      note: form.note || '',
    };
    if (mode === 'existing') {
      if (!form.target_packet_id) { toast.error('Select an existing target packet'); return; }
      payload.target_packet_id = form.target_packet_id;
    } else {
      if (!form.new_classification_id) { toast.error('Select a classification for the new packet'); return; }
      const np = {
        classification_id: form.new_classification_id,
        valuation_mode: form.new_valuation_mode,
        description: form.new_description || '',
      };
      if (form.new_valuation_mode === 'fixed') {
        const r = Number(form.new_fixed_rate);
        if (!(r > 0)) { toast.error('Fixed valuation requires a positive rate'); return; }
        np.fixed_rate_per_ct = r;
      }
      payload.new_packet = np;
    }
    setSubmitting(true);
    try {
      await api.post(`/broken-packet-issues/${issue.id}/receive`, payload);
      toast.success('Recut material received');
      onOpenChange(false);
      if (typeof onReceived === 'function') onReceived();
    } catch (e) {
      toast.error(extractError(e, 'Receive failed'));
    } finally { setSubmitting(false); }
  };

  const disabled =
    submitting ||
    rct <= 0 || rpcs <= 0 ||
    overIssued ||
    (mode === 'existing' && !form.target_packet_id) ||
    (mode === 'new' && !form.new_classification_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="receive-recut-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-800">
            <PackageCheck size={18} className="text-emerald-700" />
            Receive Recut · {issue?.issue_memo_no}
          </DialogTitle>
          <DialogDescription>
            Receiving from <b>{issue?.recipient_name}</b> ({issue?.recipient_type}). Issued
            <b> {formatCarats(issuedCt)} ct · {issuedPcs} pcs</b>. Any carat loss between
            issued and returned is captured as cutting loss.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Returned Carats <span className="text-rose-600">*</span></Label>
              <Input
                type="number" step="0.001" min="0" max={issuedCt}
                value={form.returned_carats}
                onChange={(e) => setForm({ ...form, returned_carats: e.target.value })}
                data-testid="receive-recut-carats"
              />
            </div>
            <div>
              <Label>Returned Pieces <span className="text-rose-600">*</span></Label>
              <Input
                type="number" step="1" min="0" max={issuedPcs}
                value={form.returned_pieces}
                onChange={(e) => setForm({ ...form, returned_pieces: e.target.value })}
                data-testid="receive-recut-pieces"
              />
            </div>
          </div>

          <div className="rounded-md border border-[var(--divider)] bg-slate-50 px-3 py-2 text-xs text-slate-700">
            Cutting loss: <span className="font-mono tabular-nums font-semibold">{formatCarats(cutLoss)} ct</span>
            {overIssued && (
              <span className="ml-2 text-rose-700">Returned qty exceeds issued qty.</span>
            )}
          </div>

          <div>
            <Label>Route Into <span className="text-rose-600">*</span></Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="receive-recut-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New normal packet</SelectItem>
                <SelectItem value="existing">Existing normal packet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === 'existing' && (
            <div>
              <Label>Target Packet <span className="text-rose-600">*</span></Label>
              <Select
                value={form.target_packet_id}
                onValueChange={(v) => setForm({ ...form, target_packet_id: v })}
                disabled={loading || candidates.length === 0}
              >
                <SelectTrigger data-testid="receive-recut-target">
                  <SelectValue placeholder={loading ? 'Loading…' : (candidates.length === 0 ? 'No normal packets' : 'Select existing packet…')} />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.packet_no} — {formatCarats(p.balance_carats)}ct / {p.balance_pieces}pcs
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {mode === 'new' && (
            <div className="grid grid-cols-1 gap-3 rounded-md border border-[var(--divider)] p-3 bg-white">
              <div>
                <Label>Classification <span className="text-rose-600">*</span></Label>
                <Select
                  value={form.new_classification_id}
                  onValueChange={(v) => setForm({ ...form, new_classification_id: v })}
                  disabled={loading || classifications.length === 0}
                >
                  <SelectTrigger data-testid="receive-recut-new-cls">
                    <SelectValue placeholder={loading ? 'Loading…' : 'Select…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {classifications.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.schema_code} — {c.shape_name || c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Valuation Mode</Label>
                  <Select
                    value={form.new_valuation_mode}
                    onValueChange={(v) => setForm({ ...form, new_valuation_mode: v })}
                  >
                    <SelectTrigger data-testid="receive-recut-new-val">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="running_avg">Running Average</SelectItem>
                      <SelectItem value="fixed">Fixed Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.new_valuation_mode === 'fixed' && (
                  <div>
                    <Label>Fixed Rate (₹/ct)</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={form.new_fixed_rate}
                      onChange={(e) => setForm({ ...form, new_fixed_rate: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <div>
                <Label>Description <span className="text-slate-400">(optional)</span></Label>
                <Input
                  value={form.new_description}
                  onChange={(e) => setForm({ ...form, new_description: e.target.value })}
                  placeholder="e.g. Recut of D-RB-034-BR"
                />
              </div>
            </div>
          )}

          <div>
            <Label>Note <span className="text-slate-400">(optional)</span></Label>
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={disabled} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="receive-recut-submit">
            {submitting ? 'Receiving…' : 'Confirm Receive'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
